// The Apps switch and the badge counts (apps spec §10.6), for the places
// outside Code › Apps that only need to know whether Apps is on and how
// many apps wait on the operator: the sidebar and Code tab badges, Home,
// the Settings sections, the phone's More row.
//
//   apps_enabled   the workspace settings answer (read-only, stream G);
//                  until it answers, the switch this browser remembered
//                  (features/navigation/navFlags), so a badge or a nav row
//                  does not appear a moment after the rest. Missing = off,
//                  so an api without the field is today's screens.
//   GET /api/customers/{c}/apps/summary   {down, waiting_yes, asking, building, total}
//                  ONE query key, ["apps", c, "summary"], under every
//                  ["apps", c] invalidation the Apps pages make.
//
// Code › Apps' own list and page read F1's api/apps.ts; this file only
// reads the summary, so the two never disagree on a count.

import { queryOptions, useQuery } from "@tanstack/react-query";

import { useCustomerSettings } from "@/api/customerSettings";
import { useRememberedNav } from "@/features/navigation/useNavFlags";
import { apiFetch } from "@/lib/api";

export interface AppsSummary {
  down: number;
  waiting_yes: number;
  asking: number;
  building: number;
  total: number;
  /** Additive (stream G): the flag as the server reads it. */
  enabled?: boolean;
  /** Additive: Coding steps and Jobs are on, so Build works. */
  can_build?: boolean;
  /** Additive: rows in Settings › Outside access. */
  outside?: number;
}

/** Badge counts refresh once a minute, like every other badge. */
export const APPS_SUMMARY_POLL_MS = 60_000;

export const appsSummaryKey = (customerId: string | null) => ["apps", customerId, "summary"] as const;

export function appsSummaryQuery(customerId: string | null) {
  return queryOptions({
    queryKey: appsSummaryKey(customerId),
    queryFn: ({ signal }) => apiFetch<AppsSummary>(`/api/customers/${customerId}/apps/summary`, { signal }),
    enabled: !!customerId,
    staleTime: 8_000,
    refetchInterval: APPS_SUMMARY_POLL_MS,
  });
}

/** The summary, only where Apps is on (`customerId` null = off: nothing is fetched). */
export function useAppsSummary(customerId: string | null) {
  return useQuery(appsSummaryQuery(customerId));
}

/**
 * Whether this workspace has Apps on: the live settings answer once it is
 * here, else the switch this browser remembered, else undefined (nothing
 * known yet). `=== true`, so an api that does not send the field reads off.
 */
export function useAppsFlag(customerId: string | null | undefined): boolean | undefined {
  const c = customerId || null;
  const q = useCustomerSettings(c);
  const settings = q.data as { apps_enabled?: unknown } | undefined;
  const remembered = useRememberedNav(c)?.flags;
  if (settings) return settings.apps_enabled === true;
  if (remembered?.apps_enabled !== undefined) return remembered.apps_enabled;
  // A settings read that failed with nothing remembered: today's screens, not a wait for ever.
  return q.isError ? false : undefined;
}

/** True where this workspace has Apps on (off while nothing is known). */
export function useAppsEnabled(customerId: string | null | undefined): boolean {
  return useAppsFlag(customerId) === true;
}

/** What waits on the operator: a yes, a question, an app that is down (the Code tab's count). */
export function appsWaiting(s: Pick<AppsSummary, "down" | "waiting_yes" | "asking"> | null | undefined): number {
  return s ? s.down + s.waiting_yes + s.asking : 0;
}
